import { useCallback, useEffect, useRef, useState } from "react";

// The Web Speech API isn't in TypeScript's DOM library, so the little of it we
// use is described here. Chrome (desktop and Android) exposes it under the
// webkit prefix; Safari has it too, Firefox does not.
type SpeechAlternative = { transcript: string };
type SpeechResult = { isFinal: boolean; 0: SpeechAlternative; length: number };
type SpeechResultList = { length: number; [i: number]: SpeechResult };
type SpeechEvent = { resultIndex: number; results: SpeechResultList };
type SpeechErrorEvent = { error: string };

type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechEvent) => void) | null;
  onerror: ((e: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
};

type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Chrome ends a recognition run after a few seconds of silence even with
 * `continuous`. As long as the child hasn't pressed stop we start it again, so
 * thinking mid-sentence doesn't cut the dictation short. */
const MAX_RESTARTS = 60;

const NEED_PERMISSION = "Reikia leidimo naudoti mikrofoną 🎤";
const NO_MIC = "Nerandu mikrofono. Parašyk atsakymą 🙂";
const MIC_FAILED = "Nepavyko įjungti mikrofono 🎤";
const HEARD_NOTHING = "Nepavyko išgirsti. Parašyk atsakymą 🙂";

type Options = {
  /** Recognition language. The app is Lithuanian, so that's the default. */
  lang?: string;
  /** Called with each finished chunk of speech, ready to append to the draft. */
  onFinal: (text: string) => void;
};

export type SpeechInput = ReturnType<typeof useSpeechInput>;

/**
 * Dictation for a text field, on top of the browser's speech recognition.
 *
 * Finished phrases are handed to `onFinal` (the caller appends them to its own
 * draft, so the child can still fix a word by hand); `interim` is what the
 * browser has heard so far and is meant to be shown greyed out after the draft.
 *
 * `supported` is false where the API doesn't exist — the caller should then
 * simply not show a microphone.
 */
export function useSpeechInput({ lang = "lt-LT", onFinal }: Options) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [supported] = useState(() => recognitionCtor() !== null);

  const recognitionRef = useRef<Recognition | null>(null);
  // True between "start" and the child pressing stop: tells `onend` whether the
  // run ended because of silence (restart) or because we asked it to (stop).
  const wantedRef = useRef(false);
  const restartsRef = useRef(0);
  // Whether the current run ever reached `onstart` — i.e. whether the browser
  // actually gave us the microphone. See `diagnose`.
  const capturingRef = useRef(false);
  const erroredRef = useRef(false);
  const retriedRef = useRef(false);
  // A run we ended ourselves — by the stop button or by unmounting — explains
  // its own silence, and must never make us ask for the microphone again.
  const cancelledRef = useRef(false);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  const beginRef = useRef<() => void>(() => {});

  const stop = useCallback(() => {
    wantedRef.current = false;
    cancelledRef.current = true;
    setInterim("");
    setListening(false);
    recognitionRef.current?.stop();
  }, []);

  /**
   * Why a run can end without ever having started.
   *
   * Chrome ends a recognition run instantly and *silently* — no `onstart`, no
   * `onerror`, just `onend` — whenever it has no microphone to hand over: the
   * permission prompt is still open, the site is blocked, or macOS is not
   * letting Chrome record at all. Restarting into that same silence (which is
   * what this hook used to do, sixty times over) is why the button could look
   * completely inert: nothing lit up and nothing said why.
   *
   * Asking for the stream directly is what produces a real answer, so that is
   * what a silent end triggers. A granted stream also means the prompt was
   * simply answered too late for the first run, so we try once more — which is
   * the ordinary first-ever-use path, and the one where "nothing happens" was
   * most likely to be met.
   */
  const diagnose = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(MIC_FAILED);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      // The panel may be gone by the time a slow prompt is answered.
      if (cancelledRef.current) return;
      if (retriedRef.current) {
        console.warn("[speech] microphone granted but recognition ended immediately");
        setError(MIC_FAILED);
        return;
      }
      retriedRef.current = true;
      beginRef.current();
    } catch (e) {
      const name = (e as DOMException)?.name;
      console.warn("[speech] microphone unavailable:", name);
      setError(name === "NotFoundError" || name === "OverconstrainedError" ? NO_MIC : NEED_PERMISSION);
    }
  }, []);

  const begin = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor || wantedRef.current) return;

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      capturingRef.current = true;
      setListening(true);
      setError(null);
    };

    recognition.onresult = (e) => {
      let pending = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        const text = result[0].transcript;
        if (result.isFinal) onFinalRef.current(text.trim());
        else pending += text;
      }
      setInterim(pending);
    };

    recognition.onerror = (e) => {
      // Silence isn't a failure — `onend` will restart us.
      if (e.error === "no-speech" || e.error === "aborted") return;
      console.warn("[speech] recognition error:", e.error);
      erroredRef.current = true;
      wantedRef.current = false;
      setListening(false);
      setInterim("");
      setError(
        e.error === "not-allowed" || e.error === "service-not-allowed"
          ? NEED_PERMISSION
          : e.error === "audio-capture"
            ? NO_MIC
            : HEARD_NOTHING
      );
    };

    recognition.onend = () => {
      setInterim("");
      const captured = capturingRef.current;
      // Only a run that really captured something is worth restarting. One that
      // ended without ever starting would restart into the same silence.
      if (wantedRef.current && captured && restartsRef.current < MAX_RESTARTS) {
        restartsRef.current += 1;
        capturingRef.current = false;
        try {
          recognition.start();
          return;
        } catch {
          // Fall through and stop listening.
        }
      }
      wantedRef.current = false;
      setListening(false);
      if (!captured && !erroredRef.current && !cancelledRef.current) void diagnose();
    };

    recognitionRef.current = recognition;
    wantedRef.current = true;
    restartsRef.current = 0;
    capturingRef.current = false;
    erroredRef.current = false;
    cancelledRef.current = false;
    try {
      recognition.start();
    } catch {
      wantedRef.current = false;
      setListening(false);
      setError(MIC_FAILED);
    }
  }, [lang, diagnose]);
  beginRef.current = begin;

  // A press of the button is a fresh attempt: it gets its own retry after the
  // permission prompt, however the last one ended.
  const start = useCallback(() => {
    retriedRef.current = false;
    begin();
  }, [begin]);

  const toggle = useCallback(() => (wantedRef.current ? stop() : start()), [start, stop]);

  // Never leave the microphone running behind us.
  useEffect(
    () => () => {
      wantedRef.current = false;
      cancelledRef.current = true;
      recognitionRef.current?.abort();
    },
    []
  );

  return { supported, listening, interim, error, start, stop, toggle };
}
